import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData, QueryKey } from '@tanstack/react-query';
import { Assessment, AssessmentsResponse, AssessmentStatusTotals } from '@app-types';

interface UseAssessmentPageDataOptions {
  queryKey: QueryKey;
  queryFn: () => Promise<AssessmentsResponse>;
  pageSize: number; // Required for correct totalPages calculation
  enabled?: boolean;
}

export interface UseAssessmentPageDataReturn {
  // Data
  assessments: Assessment[];
  isLoading: boolean;
  error: Error | null;
  isFetching: boolean;

  // Response
  response: AssessmentsResponse | undefined;

  // Status totals
  filterCounts: Record<string, number>;

  // Pagination
  totalItems: number;
  totalPages: number;

  // Actions
  refreshData: () => void;
}

/**
 * Generic hook for data fetching in assessment pages
 * Handles: data fetching, statusTotals preservation, filter counts, pagination
 * Same implementation as useSchedulePageData but for assessments
 */
export function useAssessmentPageData(options: UseAssessmentPageDataOptions): UseAssessmentPageDataReturn {
  const { queryKey, queryFn, pageSize, enabled = true } = options;
  const queryClient = useQueryClient();

  // Fetch data with placeholderData to prevent UI flicker
  const {
    data: response,
    isLoading,
    error,
    isFetching
  } = useQuery<AssessmentsResponse>({
    queryKey,
    queryFn,
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: keepPreviousData,
    enabled
  });

  // Extract assessments
  const assessments = useMemo(() => response?.data || [], [response]);

  // Preserve last known totals to avoid counter flicker between filter changes
  const [lastStatusTotals, setLastStatusTotals] = useState<AssessmentStatusTotals | null>(null);
  useEffect(() => {
    if (response?.statusTotals) {
      setLastStatusTotals(response.statusTotals);
    }
  }, [response?.statusTotals]);

  // Calculate filter counts from statusTotals
  const filterCounts: Record<string, number> = useMemo(() => {
    const totals = response?.statusTotals || lastStatusTotals;

    if (!totals) {
      return {
        all: response?.total || 0,
        Pending: 0,
        'Pending Evidence': 0,
        'Evidence Past Due': 0,
        Escalated: 0,
        Final: 0,
        Cancelled: 0,
        Expired: 0,
        conflicts: 0,
        conflictsTotal: 0
      };
    }

    // Use 'all' from backend if available, otherwise fall back to total
    return {
      all: totals.all || response?.total || 0,
      Pending: totals.Pending || 0,
      'Pending Evidence': totals['Pending Evidence'] || 0,
      'Evidence Past Due': totals['Evidence Past Due'] || 0,
      Escalated: totals.Escalated || 0,
      Final: totals.Final || 0,
      Cancelled: totals.Cancelled || 0,
      Expired: totals.Expired || 0,
      conflicts: totals.conflicts || 0,
      conflictsTotal: totals.conflictsTotal || totals.conflicts || 0
    };
  }, [response, lastStatusTotals]);

  // Pagination calculations
  const totalItems = response?.total || 0;
  const totalPages = Math.ceil(totalItems / (pageSize || 1));

  // Refresh data callback
  const refreshData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [queryKey[0]] });
  }, [queryClient, queryKey]);

  return {
    // Data
    assessments,
    isLoading,
    error: error as Error | null,
    isFetching,

    // Response
    response,

    // Status totals
    filterCounts,

    // Pagination
    totalItems,
    totalPages,

    // Actions
    refreshData
  };
}
